import React, { useState, useEffect, useRef } from 'react';
import { checkCacheLimit } from '../utils/cacheUtils';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  loading?: 'lazy' | 'eager';
}

const LazyImage: React.FC<LazyImageProps> = ({ src, alt, className, style, loading = 'lazy' }) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const cacheKey = `img_${src}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      setImageSrc(cached);
      setIsLoaded(true);
      return;
    }

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);
      
      try {
        checkCacheLimit();
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        localStorage.setItem(cacheKey, dataUrl);
        setImageSrc(dataUrl);
      } catch (e) {
        setImageSrc(src);
      }
      setIsLoaded(true);
    };
    img.onerror = () => {
      setImageSrc(src);
      setIsLoaded(true);
    };
    img.src = src;
  }, [src]);

  return (
    <img
      ref={imgRef}
      src={imageSrc}
      alt={alt}
      className={className}
      style={{ ...style, opacity: isLoaded ? 1 : 0, transition: 'opacity 0.3s' }}
      loading={loading}
    />
  );
};

export default LazyImage;
